import { useEffect, useRef, type RefObject } from 'react';
import { io, type Socket } from 'socket.io-client';
import { SERVER_BASE_URL } from './config';
import { currentOrgHeader } from '../stores/orgContextStore';

/** What changed on the server. The event carries no values — only what to refetch. */
export type ProductionChangeKind = 'status' | 'comment' | 'booking' | 'signature' | 'step';

export const ALL_PRODUCTION_CHANGE_KINDS: ProductionChangeKind[] = [
  'status',
  'comment',
  'booking',
  'signature',
  'step',
];

/** The casting equivalent: a decision is an accept/reject mark, a model an add/remove. */
export type CastingChangeKind = 'comment' | 'decision' | 'model' | 'status';

/** Tickets (FEATURE_TICKETS). One room per ORGANIZATION — list, badge and detail refresh from it. */
export type TicketChangeKind = 'created' | 'status' | 'assignee' | 'comment' | 'link';
export const ALL_TICKET_CHANGE_KINDS: TicketChangeKind[] = ['created', 'status', 'assignee', 'comment', 'link'];

export const ALL_CASTING_CHANGE_KINDS: CastingChangeKind[] = [
  'comment',
  'decision',
  'model',
  'status',
];

/** One dev-only line per socket, so a blocked proxy does not fill the console. */
const devLog = (msg: string) => {
  if (import.meta.env.DEV) console.info(`[realtime] ${msg}`);
};

/**
 * The wire names of one channel. Productions and castings follow the same protocol over the
 * same socket but with their own event names — the production ones are deployed and must stay
 * exactly as they are, so the names live in data rather than being derived from the entity.
 */
interface ChannelSpec<K extends string> {
  /** Only used in the dev log line. */
  name: string;
  joinEvent: string;
  leaveEvent: string;
  changedEvent: string;
  /** Field naming the entity in the join/leave payload and on the change event. */
  idField: string;
  allKinds: K[];
}

const PRODUCTION_CHANNEL: ChannelSpec<ProductionChangeKind> = {
  name: 'production',
  joinEvent: 'join',
  leaveEvent: 'leave',
  changedEvent: 'production:changed',
  idField: 'production_id',
  allKinds: ALL_PRODUCTION_CHANGE_KINDS,
};

const TICKETS_CHANNEL: ChannelSpec<TicketChangeKind> = {
  name: 'tickets',
  joinEvent: 'join_tickets',
  leaveEvent: 'leave_tickets',
  changedEvent: 'tickets:changed',
  idField: 'organization_id',
  allKinds: ALL_TICKET_CHANGE_KINDS,
};

const CASTING_CHANNEL: ChannelSpec<CastingChangeKind> = {
  name: 'casting',
  joinEvent: 'join_casting',
  leaveEvent: 'leave_casting',
  changedEvent: 'casting:changed',
  idField: 'casting_id',
  allKinds: ALL_CASTING_CHANGE_KINDS,
};

/**
 * Follow one entity's changes over the socket. Everything that makes this work is channel
 * agnostic — the debounce, the refetch-everything reconnect, the silent failure — so both
 * public hooks are thin wrappers over this and cannot drift apart.
 *
 * `onChange` receives a coalesced set of kinds and is called at most once per debounce
 * window: a bulk action on the server emits once per handler, and several people acting at
 * the same time overlap, so the raw events arrive in bursts that must not turn into a burst
 * of refetches. The set is the union of everything seen in the window.
 *
 * The socket is only ever a *hint* that the page is stale — nothing here is required for it
 * to work. A handshake the server denies (expired token) or a proxy that blocks the upgrade
 * leaves the caller on its own polling with no user-visible sign of failure.
 *
 * Returns a ref that turns true on the first real `connect` and never turns back off, so a
 * caller can slow its own fallback poll down only once it has proof the socket works in this
 * environment. A ref rather than state on purpose: this is read inside timers, and re-rendering
 * a caller — or letting a reconnect loop re-render it repeatedly — buys nothing.
 */
function useChannelRealtime<K extends string>(
  channel: ChannelSpec<K>,
  entityId: number | null | undefined,
  onChange: (kinds: Set<K>) => void,
  debounceMs: number
): RefObject<boolean> {
  // Callers pass an inline handler; through a ref the socket is not torn down and rebuilt
  // on every render of a 2500-line page.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  /**
   * "The socket has worked here at least once." Sticky for the life of the page, including
   * across a change of entity: it answers whether this deployment and this network allow
   * /socket.io at all, and that answer does not go stale when a connection drops — a drop is
   * exactly when the fallback poll matters and socket.io is already retrying.
   */
  const everConnected = useRef(false);

  useEffect(() => {
    if (!entityId) return;
    // The same token the API client sends — impersonation swaps it in place, so reading it
    // here (rather than caching it) keeps the socket on whoever the app is acting as.
    const token = localStorage.getItem('token');
    if (!token) return;

    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pending: Set<K> | null = null;

    const flush = () => {
      timer = null;
      const kinds = pending;
      pending = null;
      if (!disposed && kinds && kinds.size) onChangeRef.current(kinds);
    };

    const schedule = (kinds: K[]) => {
      if (!pending) pending = new Set();
      for (const k of kinds) pending.add(k);
      // Trailing edge only: a burst should settle before we ask the server for the result.
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, debounceMs);
    };

    const socket: Socket = io(SERVER_BASE_URL, {
      auth: {
        token,
        // The socket carries no org-scoped data, but the server still has to authorise the
        // join for multi-org and super admins the way X-Organization-Id does for REST.
        // It goes in the handshake, not in extraHeaders: headers only survive the polling
        // transport and would vanish on the websocket upgrade.
        organization_id: currentOrgHeader(),
      },
      // Fewer round trips to a live socket, and no long-polling requests left hanging in a
      // proxy that would not have upgraded them anyway.
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      // Only a completed handshake counts — never "we built a socket" or "we are attempting".
      everConnected.current = true;
      socket.emit(channel.joinEvent, { [channel.idField]: entityId });
      // The cornerstone of the design: a reconnect gets a new socket id and an empty room,
      // and every event emitted while we were away is gone for good — the server keeps no
      // backlog. So treat any successful (re)connect as "assume all of it changed" and
      // refetch at once. This also covers the very first connect, which costs one request
      // and closes the gap between the initial load and the socket being live.
      schedule(channel.allKinds);
    });

    socket.on(channel.changedEvent, (payload: Record<string, unknown> | undefined) => {
      // Other entities share the connection if the server ever broadcasts wider.
      if (payload?.[channel.idField] !== entityId) return;
      const raw = payload?.kinds;
      const kinds = Array.isArray(raw) ? (raw as K[]) : [];
      if (!kinds.length) return;
      schedule(kinds);
    });

    let reportedFailure = false;
    socket.on('connect_error', (err) => {
      // `socket.active` false means a middleware refused the handshake (expired token) and
      // socket.io will not retry — reconnecting by hand would only loop on the same refusal.
      // Either way the page stays on its poll, so this is not the user's problem. Retries
      // fire this repeatedly, so say it once: a page left open must not fill the console.
      if (reportedFailure) return;
      reportedFailure = true;
      devLog(`${channel.name} unavailable, falling back to polling (${err.message})`);
    });

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      if (socket.connected) socket.emit(channel.leaveEvent, { [channel.idField]: entityId });
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [channel, entityId, debounceMs]);

  return everConnected;
}

/** Follow one production's changes. See {@link useChannelRealtime} for the how and why. */
export function useProductionRealtime(
  productionId: number | null | undefined,
  onChange: (kinds: Set<ProductionChangeKind>) => void,
  debounceMs = 300
): RefObject<boolean> {
  return useChannelRealtime(PRODUCTION_CHANNEL, productionId, onChange, debounceMs);
}

/**
 * Follow one casting's changes. Both sides of the conversation sit on this page — the agency
 * on the org mount and the client on the pool_admin mount — so a single subscription is what
 * makes an accept, a reject or a note appear on the other screen without a reload.
 */
export function useCastingRealtime(
  castingId: number | null | undefined,
  onChange: (kinds: Set<CastingChangeKind>) => void,
  debounceMs = 300
): RefObject<boolean> {
  return useChannelRealtime(CASTING_CHANNEL, castingId, onChange, debounceMs);
}

/**
 * Follow every ticket change of one organization. The room is per org, not per ticket, so the
 * list page, the header badge and an open detail panel share a single subscription.
 */
export function useTicketsRealtime(
  organizationId: number | null | undefined,
  onChange: (kinds: Set<TicketChangeKind>) => void,
  debounceMs = 300
): RefObject<boolean> {
  return useChannelRealtime(TICKETS_CHANNEL, organizationId, onChange, debounceMs);
}
